/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-26 08:00:56
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { useState } from 'react';
import { Field, FieldArray } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import BannerCustom from 'src/components/views/common/banner-custom';
import ConfirmModal from '../common/confirm-modal';

import OptionsTable from './options-table';
import ProductSuggestionDisplay from './product-suggestion-display';
import ScopedSuggestion from 'src/components/views/instant-search-widget/isw-with-results/scoped-suggestion';
import ScopeSuggestionsCardLock from './scope-suggestions-card-lock';
import { FormLayout, TextStyle } from '@shopify/polaris';
import { routes } from 'src/routes';

const numberOptions = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '10', label: '10' }
];
// DONE
const InstantSearchWidget = props => {
  const {
    i18n,
    history,
    enableProducts,
    suggestionBlocks,
    suggestionStyle,
    updateField,
    collections,
    formValues,
    subscribedAddonAIPowerSearch,
    disableLockUnLock,
    subscribedAddonBefore,
    handleUnlock,
    isDisableSuggestion,
    notThemeSetupV1,
    searchVersion
  } = props;
  const [activeModalConfirm, setActiveModalConfirm] = useState(false);

  const indexProduct = _.findIndex(suggestionBlocks, item => item.type === 'products');
  const indexCollections = _.findIndex(suggestionBlocks, item => item.type === 'collections');
  const indexSuggestions = _.findIndex(suggestionBlocks, item => item.type === 'suggestions');
  const indexPages = _.findIndex(suggestionBlocks, item => item.type === 'pages');
  const collectionExcludedValues = _.get(
    formValues,
    `suggestionBlocks[${indexCollections}].excludedValues`,
    []
  );

  const statusAddon = subscribedAddonBefore ? 'restore' : 'trial';

  const handleChangeStatus = e => {
    // Handle change status
    if (suggestionStyle !== 'style1' && enableProducts && notThemeSetupV1) {
      e.preventDefault();
      setActiveModalConfirm(true);
    }
  };

  // Handle disable product
  const handleContinueTurnOffProducts = () => {
    const index = _.findIndex(suggestionBlocks, item => item.type === 'products');
    if (index !== -1) {
      updateField(`suggestionBlocks[${index}].status`, false);
      updateField('suggestionStyle', 'style1');
    }
    setActiveModalConfirm(false);
  };
  return (
    <div className="isw-with-results">
      <FormLayout>
        <p>
          {i18n.translate('AdminHTML.InstantSearchDisplay.ISWWithResults.contentDisplay_Label')}
        </p>
        {/* Display product */}
        <Field
          name={`suggestionBlocks[${indexProduct}].status`}
          parse={value => (value ? 'active' : 'disabled')}
          format={value => value === 'active'}
          onChange={e => handleChangeStatus(e)}
          component={RenderToggleSwitch}
          label={i18n.translate(
            'AdminHTML.InstantSearchDisplay.ISWWithResults.displayProduct_Label'
          )}
        />
        {enableProducts && (
          <div className="groupFields">
            <div className="groupFields-item">
              <Field
                name={`suggestionBlocks[${indexProduct}].label`}
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.SearchBoxOnClick.searchBoxRecentSearchLabel_Label'
                )}
              />
              <Field
                name={`suggestionBlocks[${indexProduct}].number`}
                component={RenderSelect}
                options={numberOptions}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.SearchBoxOnClick.searchBoxRecentSearchNumber_Label'
                )}
              />
            </div>
            <ProductSuggestionDisplay />
          </div>
        )}

        {/* Display collections */}
        <Field
          name={`suggestionBlocks[${indexCollections}].status`}
          parse={value => (value ? 'active' : 'disabled')}
          format={value => value === 'active'}
          component={RenderToggleSwitch}
          label={i18n.translate(
            'AdminHTML.InstantSearchDisplay.ISWWithResults.displayCollection_Label'
          )}
        />
        {_.get(suggestionBlocks, `[${indexCollections}].status`, 'active') === 'active' && (
          <>
            <div className="groupFields">
              <div className="groupFields-item">
                <Field
                  name={`suggestionBlocks[${indexCollections}].label`}
                  component={RenderTextField}
                  label={i18n.translate(
                    'AdminHTML.InstantSearchDisplay.ISWWithResults.displayCollection_Field01'
                  )}
                />
                <Field
                  name={`suggestionBlocks[${indexCollections}].number`}
                  component={RenderSelect}
                  options={numberOptions}
                  label={i18n.translate(
                    'AdminHTML.InstantSearchDisplay.ISWWithResults.displayCollection_Field02'
                  )}
                />
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <Field
                name={`suggestionBlocks[${indexCollections}].excludedValues`}
                component={RenderAutocomplete}
                options={collections}
                extraSearchFields={['value', 'handle']}
                value={collectionExcludedValues}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.ISWWithResults.displayCollection_Field03'
                )}
                placeholder={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.ISWWithResults.displayCollection_Field03_Placeholder'
                )}
              />
              <TextStyle variation="subdued">
                {i18n.translate(
                  'AdminHTML.InstantSearchDisplay.ISWWithResults.displayCollection_Field03_Subtext'
                )}
              </TextStyle>
            </div>
          </>
        )}

        {/* Display popular suggestion */}
        <Field
          name={`suggestionBlocks[${indexSuggestions}].status`}
          parse={value => (value ? 'active' : 'disabled')}
          format={value => value === 'active'}
          component={RenderToggleSwitch}
          label={i18n.translate(
            'AdminHTML.InstantSearchDisplay.ISWWithResults.displayPopularSuggestion_Label'
          )}
        />
        {_.get(suggestionBlocks, `[${indexSuggestions}].status`, 'active') === 'active' && (
          <div className="groupFields">
            <div className="groupFields-item">
              <Field
                name={`suggestionBlocks[${indexSuggestions}].label`}
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.SearchBoxOnClick.searchBoxRecentSearchLabel_Label'
                )}
              />
              <Field
                name={`suggestionBlocks[${indexSuggestions}].number`}
                component={RenderSelect}
                options={numberOptions}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.SearchBoxOnClick.searchBoxRecentSearchNumber_Label'
                )}
              />
            </div>
            <div className="groupFields-item-scope">
              {subscribedAddonAIPowerSearch && (
                <>
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.ISWWithResults.scopedSuggestion_Title'
                    )}
                  </TextStyle>
                  <p>
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.ISWWithResults.scopedSuggestion_Subtext'
                    )}
                  </p>
                  <ScopedSuggestion isDisableSuggestion={isDisableSuggestion} />
                </>
              )}
              {!subscribedAddonAIPowerSearch && (
                <ScopeSuggestionsCardLock
                  statusAddon={statusAddon}
                  handleUnlock={handleUnlock}
                  disableLockUnLock={disableLockUnLock}
                />
              )}
            </div>
          </div>
        )}

        {/* Display blog and page */}
        <Field
          name={`suggestionBlocks[${indexPages}].status`}
          parse={value => (value ? 'active' : 'disabled')}
          format={value => value === 'active'}
          component={RenderToggleSwitch}
          label={i18n.translate(
            'AdminHTML.InstantSearchDisplay.ISWWithResults.displayBlogPage_Label'
          )}
        />
        {_.get(suggestionBlocks, `[${indexPages}].status`, 'active') === 'active' && (
          <div className="groupFields">
            <div className="groupFields-item">
              <Field
                name={`suggestionBlocks[${indexPages}].label`}
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.SearchBoxOnClick.searchBoxRecentSearchLabel_Label'
                )}
              />
              <Field
                name={`suggestionBlocks[${indexPages}].number`}
                component={RenderSelect}
                options={numberOptions}
                label={i18n.translate(
                  'AdminHTML.InstantSearchDisplay.SearchBoxOnClick.searchBoxRecentSearchNumber_Label'
                )}
              />
            </div>
          </div>
        )}
      </FormLayout>
      <div className="content-order">
        {/* -----------Content order -------------- */}
        <p>{i18n.translate('AdminHTML.InstantSearchDisplay.ISWWithResults.contentOrder_Label')}</p>
        <label>
          {i18n.translate('AdminHTML.InstantSearchDisplay.ISWWithResults.contentOrder_Subtext')}
        </label>

        <FieldArray
          name="suggestionBlocks"
          component={OptionsTable}
          suggestionBlocks={suggestionBlocks}
          suggestionStyle={suggestionStyle}
          updateField={updateField}
        />
        {/* ---------- Banner ------------------ */}
        {searchVersion === '2.0' && (
          <BannerCustom
            idDismiss="iswWithResultTabCustomBanner"
            title={i18n.translate('AdminHTML.InstantSearchDisplay.ISWWithResults.bannerTitle')}
            action={() => history.push(routes.merchandising.view())}
            actionContent={i18n.translate(
              'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerAction'
            )}>
            <p>
              {i18n.translate('AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent', {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent_t1'
                    )}
                  </TextStyle>
                ),
                t2: (
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent_t2'
                    )}
                  </TextStyle>
                ),
                t3: (
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent_t3'
                    )}
                  </TextStyle>
                )
              })}
            </p>
          </BannerCustom>
        )}
      </div>
      {/* Confirm disable modal */}
      <ConfirmModal
        active={activeModalConfirm}
        handleContinue={handleContinueTurnOffProducts}
        handleCancel={() => setActiveModalConfirm(false)}
        title={i18n.translate('AdminHTML.InstantSearchDisplay.ConfirmModal.disableProducts_Title')}
        content={i18n.translate(
          'AdminHTML.InstantSearchDisplay.ConfirmModal.disableProducts_Content'
        )}
        actionLabel={i18n.translate(
          'AdminHTML.InstantSearchDisplay.ConfirmModal.disableProducts_Action'
        )}
        actionDestructive={true}
      />
    </div>
  );
};
export default withI18n()(InstantSearchWidget);
